import React from "react";
import { Box, Typography, Theme, useTheme } from "@mui/material";
import { CircleAlert, Info } from "lucide-react";

interface BannerVariant {
  icon: React.ElementType;
  getColors: (theme: Theme) => { background: string; border: string };
}

// Per aggiungere un nuovo tipo (es. success, error) basta una nuova voce qui:
// `BannerType` e il rendering si adeguano senza altre modifiche.
const BANNER_VARIANTS = {
  info: {
    icon: Info,
    getColors: (theme) => ({
      background: theme.palette.backgroundSecondaryAlternative,
      border: theme.palette.borderPrimaryCta,
    }),
  },
  warning: {
    icon: CircleAlert,
    getColors: (theme) => ({
      background: theme.palette.backgroundWarning,
      border: theme.palette.borderWarning,
    }),
  },
} satisfies Record<string, BannerVariant>;

export type BannerType = keyof typeof BANNER_VARIANTS; // "info" | "warning"

interface BannerCstProps {
  label: React.ReactNode;
  /** Determina icona e colori (sfondo, bordo, icona). Default: info. */
  type?: BannerType;
  /** Override opzionali sui valori del tipo. */
  icon?: React.ReactNode;
  backgroundColor?: string;
  borderColor?: string;
  /** Default: colore del bordo del tipo. */
  iconColor?: string;
  textColor?: string;
  sx?: Record<string, any>;
}

const BannerCst: React.FC<BannerCstProps> = ({
  label,
  type = "info",
  icon,
  backgroundColor,
  borderColor,
  iconColor,
  textColor,
  sx,
}) => {
  const theme = useTheme();
  const variant: BannerVariant = BANNER_VARIANTS[type];
  const colors = variant.getColors(theme);
  const resolvedBorder = borderColor ?? colors.border;
  const Icon = variant.icon;

  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        padding: "12px 16px",
        borderRadius: "4px",
        backgroundColor: backgroundColor ?? colors.background,
        border: `1px solid ${resolvedBorder}`,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexShrink: 0,
          color: iconColor ?? resolvedBorder,
        }}
      >
        {icon ?? <Icon size={24} />}
      </Box>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: textColor ?? theme.palette.textTag,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default BannerCst;
